import { useState, useEffect, useCallback } from 'react';
import Icon from '@material-ui/core/Icon';
import { apiClient } from '@services/api';
import { useHistory } from 'react-router-dom';

const POLL_INTERVAL = 30000; // 30s — mesmo intervalo do NotificationBell

/**
 * Ícone de reembolso ao lado do sino (2026-08-13, 3 cores — 2026-08-13 ampliado).
 * Regra de negócio (prioridade vermelho > azul > verde):
 *   - VERMELHO: existe pedido `pending` — precisa de assinatura humana (dupla custódia
 *     cruzada marcelo@/logistica@, não importa qual dos dois falta assinar).
 *   - AZUL: não há pending, mas existe pedido `approved` sem comprovante ainda — dupla
 *     custódia interna já cumprida, mas a Asaas está com o estorno em espera (comum em
 *     PIX, autorização manual no painel deles). Não precisa de ação aqui, só aguardar —
 *     mas o time (solicitante E aprovador, os únicos que decidem, mas TODOS que veem o
 *     ícone) precisa saber que existe transação parada no banco.
 *   - VERDE: tudo certo, nada pendente e nada esperando o banco.
 * Fonte: GET /refunds/status (RefundController::status — não escopado por vendedor).
 *
 * Clique leva pra /admin/reembolsos — mesma tela de sempre, agora acessível a todo
 * consultor ativo (menu subiu de "Administrar" pra "Geral"), mas cada um só vê os
 * próprios pedidos + comprovante ali dentro; Liberar/Recusar continuam exclusivos de
 * marcelo@/logistica@ (escondidos na própria tela pra quem não é aprovador).
 */
export default function RefundStatusIcon() {
  const [pending, setPending] = useState(0);
  const [awaitingBank, setAwaitingBank] = useState(0);
  const history = useHistory();

  const fetchStatus = useCallback(() => {
    apiClient.httpInstance
      .get('/refunds/status')
      .then(res => {
        if (res.status === 200) {
          setPending(res.data?.pending ?? 0);
          setAwaitingBank(res.data?.awaiting_bank ?? 0);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const isRed  = pending > 0;
  const isBlue = !isRed && awaitingBank > 0;
  const color  = isRed ? '#ef4444' : (isBlue ? '#38bdf8' : '#4ade80');
  const badgeCount = isRed ? pending : (isBlue ? awaitingBank : 0);

  const title = isRed
    ? `${pending} pedido(s) de estorno aguardando assinatura`
    : (isBlue ? `${awaitingBank} estorno(s) aprovado(s) aguardando confirmação do banco/Asaas` : 'Reembolsos em dia');

  return (
    <button
      onClick={() => history.push('/admin/reembolsos')}
      style={{
        position: 'relative',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '6px',
        borderRadius: '8px',
        color: '#8ba5c4',
        display: 'flex',
        alignItems: 'center',
        transition: 'color 0.15s',
      }}
      title={title}
    >
      {/* Mesmo ícone do menu lateral ("replay") sempre — só a COR muda, igual ao padrão
          de semáforo do sino de notificações. Não troca de símbolo (isso confundia —
          2026-08-13). */}
      <Icon style={{ fontSize: '22px', color }}>replay</Icon>
      {badgeCount > 0 && (
        <span style={{
          position: 'absolute',
          top: '2px',
          right: '2px',
          minWidth: '16px',
          height: '16px',
          borderRadius: '8px',
          background: color,
          color: '#0f2340',
          fontSize: '10px',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 3px',
          lineHeight: 1,
        }}>
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      )}
    </button>
  );
}
